import { defineStore } from 'pinia'
import { useLogger } from '../composables/useLogger'
import type { CacheEntry, CacheState, FormFieldOption } from '../types/entityConfig'

const logger = useLogger('cacheStore')

/** Default TTL values in milliseconds */
export const DEFAULT_TTL = {
  entities: 5 * 60 * 1000,   // 5 minutes
  lookups: 10 * 60 * 1000,   // 10 minutes
  filters: 10 * 60 * 1000,   // 10 minutes
}

function isExpired<T>(entry: CacheEntry<T>): boolean {
  return Date.now() - entry.timestamp > entry.ttl
}

export const useCacheStore = defineStore('cache', {
  state: (): CacheState => ({
    entities: new Map<string, CacheEntry<any[]>>(),
    lookups: new Map<string, CacheEntry<Map<any, any>>>(),
    filters: new Map<string, CacheEntry<FormFieldOption[]>>(),
  }),

  getters: {
    entityCacheSize: (state): number => state.entities.size,
    lookupCacheSize: (state): number => state.lookups.size,
    filterCacheSize: (state): number => state.filters.size,
  },

  actions: {
    /**
     * Get cached data or fetch it using the provided function.
     * Checks the appropriate map based on `type`, returns cached data if
     * present and not expired, otherwise calls `fetchFn` and caches the result.
     */
    async getOrFetch<T>(
      type: 'entities' | 'lookups' | 'filters',
      key: string,
      fetchFn: () => Promise<T>,
      ttl?: number,
    ): Promise<T> {
      const map = this[type] as Map<string, CacheEntry<any>>
      const existing = map.get(key)

      if (existing) {
        if (!isExpired(existing)) {
          const age = Date.now() - existing.timestamp
          logger.debug('Cache hit', { operation: 'getOrFetch', cacheKey: key, ageMs: age })
          return existing.data as T
        }
        // Expired — evict
        map.delete(key)
        logger.debug('Cache entry expired and evicted', {
          operation: 'getOrFetch',
          cacheKey: key,
          ttl: existing.ttl,
        })
      } else {
        logger.debug('Cache miss', { operation: 'getOrFetch', cacheKey: key })
      }

      const data = await fetchFn()
      const effectiveTtl = ttl ?? DEFAULT_TTL[type]

      map.set(key, {
        data,
        timestamp: Date.now(),
        ttl: effectiveTtl,
      })

      logger.debug('Cache entry written', {
        operation: 'getOrFetch',
        cacheKey: key,
        ttl: effectiveTtl,
      })

      return data
    },

    /** Clear all caches or a specific cache type / key. */
    clearCache(type?: 'entities' | 'lookups' | 'filters', key?: string): void {
      if (!type) {
        this.entities.clear()
        this.lookups.clear()
        this.filters.clear()
        logger.debug('All caches cleared', { operation: 'clearCache' })
        return
      }

      const map = this[type] as Map<string, CacheEntry<any>>
      if (key) {
        map.delete(key)
        logger.debug('Cache entry cleared', { operation: 'clearCache', cacheKey: key })
      } else {
        map.clear()
        logger.debug('Cache type cleared', { operation: 'clearCache', cacheType: type })
      }
    },
  },
})
